import React, { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_URL } from '../config/urlConfig';
import { apiFetch } from '../config/api';
import { planConfigs } from '../config/planConfigs';
import { PRECOS, priceIdFor } from '../config/stripePrices';
import { GoogleMark } from '../components/AuthLayout';
import Logo from '../components/Logo';
import FotoDaEntrada from '../components/entrada/FotoDaEntrada';
import SeletorDeIdioma from '../components/SeletorDeIdioma';
import { Link, useIdioma } from '../i18n';
import { useFormato } from '../i18n/formato';
import '../styles/NewHome.css';
import '../styles/NewLogin.css';
import '../styles/NewSignup.css';

/**
 * Criação de conta, na moldura do NewLogin. Convive com `pages/Signup.js` sem
 * tocar nele: qual das duas atende `/signup` é o import em `App.js`.
 *
 * O fluxo é o mesmo: confere o e-mail, cria a sessão de checkout do Basic e
 * manda para o Stripe; a senha é definida em /complete-signup, depois. O que
 * muda é dizer isso antes do botão, manter o botão ocupado até a página sair
 * (um segundo clique criava outra sessão de checkout) e dar o erro por status,
 * em vez de repassar o texto da API ou o "Failed to fetch" do navegador.
 */

const BASIC = planConfigs.Basic;
// shortcut: o texto e a frase do Stripe dizem "sem cartão" fixo; se o Basic deixar de custar 0, trocar as duas.
const GRATIS = PRECOS.Basic.mensal === 0;

const NewSignup = () => {
  const { t } = useIdioma();
  const { numero, moeda } = useFormato();
  const nomeRef = useRef(null);
  const emailRef = useRef(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busca] = useSearchParams();
  /* { chave, campo }: campo é 'nome', 'email' ou null (falha que não é do que foi digitado).
     Quem tenta entrar pelo Google sem ter conta chega aqui como /signup?error=no_account. */
  const [erro, setErro] = useState(() =>
    busca.get('error') === 'no_account' ? { chave: 'cadastro.semContaGoogle', campo: null } : null
  );
  const [enviando, setEnviando] = useState(false);
  const [indoAoGoogle, setIndoAoGoogle] = useState(false);

  const falhar = (chave, campo) => {
    setEnviando(false);
    setErro({ chave, campo });
    if (campo === 'nome') nomeRef.current.focus();
    if (campo === 'email') emailRef.current.focus();
  };

  const criar = async (e) => {
    e.preventDefault();
    if (enviando) return;
    setErro(null);
    if (!name.trim()) return falhar('cadastro.faltaNome', 'nome');
    if (!emailRef.current.validity.valid) return falhar('cadastro.faltaEmail', 'email');

    setEnviando(true);
    try {
      const conferencia = await apiFetch('/auth/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (!conferencia.ok) {
        if (conferencia.status === 429) return falhar('entrada.erroMuitas', null);
        const { exists } = await conferencia.json().catch(() => ({}));
        return falhar(exists ? 'cadastro.emailExiste' : 'cadastro.faltaEmail', 'email');
      }

      const resposta = await apiFetch('/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name,
          priceId: priceIdFor('Basic', 'monthly'),
          fromPlans: false,
          signupMethod: 'email'
        })
      });
      if (!resposta.ok) return falhar(resposta.status === 429 ? 'entrada.erroMuitas' : 'cadastro.erroCheckout', null);

      const { url } = await resposta.json();
      // O botão fica ocupado até o navegador trocar de página.
      window.location.href = url;
    } catch {
      falhar('entrada.erroRede', null);
    }
  };

  const invalido = (campo) => (erro && erro.campo === campo) || undefined;
  const descrito = erro ? 'cadastro-erro' : undefined;

  return (
    <div className="nh nh-grade entrada cadastro">
      <FotoDaEntrada>
        <div className="cadastro-plano" data-ui>
          <p className="cadastro-plano-titulo">{t('cadastro.planoTitulo')}</p>
          <dl>
            <div>
              <dt>{t('cadastro.planoProjetos')}</dt>
              <dd>{numero(BASIC.limits.projects)}</dd>
            </div>
            <div>
              <dt>{t('cadastro.planoPaginas')}</dt>
              <dd>{numero(BASIC.limits.pages)}</dd>
            </div>
            <div>
              <dt>{t('cadastro.planoHistorico')}</dt>
              <dd>
                {t('cadastro.planoDias', {
                  n: numero(BASIC.features.data_retention_days),
                  quantidade: BASIC.features.data_retention_days
                })}
              </dd>
            </div>
            <div>
              <dt>{t('cadastro.planoCreditos')}</dt>
              <dd>{numero(BASIC.features.monthly_credits)}</dd>
            </div>
            <div>
              <dt>{t('cadastro.planoPreco')}</dt>
              <dd>{GRATIS ? t('cadastro.planoGratis') : moeda(PRECOS.Basic.mensal * 100, 'usd')}</dd>
            </div>
          </dl>
        </div>
      </FotoDaEntrada>

      <main className="entrada-principal">
        <div className="entrada-caixa">
          {/* Só no telefone, onde o painel da foto (e a marca dentro dele) sai. */}
          <header className="entrada-topo">
            <Link to="/">
              <Logo size={28} />
            </Link>
          </header>

          <h1>{t('cadastro.titulo')}</h1>
          <p className="entrada-texto">
            {t('cadastro.texto', {
              projetos: t('cadastro.projetos', {
                n: numero(BASIC.limits.projects),
                quantidade: BASIC.limits.projects
              }),
              paginas: numero(BASIC.limits.pages)
            })}
          </p>

          <a
            href={`${API_URL}/auth/google/signup`}
            className="entrada-google"
            aria-busy={indoAoGoogle || undefined}
            aria-describedby="cadastro-consentimento"
            onClick={() => setIndoAoGoogle(true)}
          >
            <GoogleMark />
            {t(indoAoGoogle ? 'entrada.abrindoGoogle' : 'entrada.comGoogle')}
          </a>
          <p className="entrada-ou">{t('cadastro.ouEmail')}</p>

          <form className="entrada-form" onSubmit={criar} noValidate>
            <div className="entrada-campo">
              <label htmlFor="cadastro-nome">{t('cadastro.nome')}</label>
              <input
                id="cadastro-nome"
                ref={nomeRef}
                name="name"
                type="text"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                aria-invalid={invalido('nome')}
                aria-describedby={descrito}
              />
            </div>

            <div className="entrada-campo">
              <label htmlFor="cadastro-email">{t('entrada.email')}</label>
              <input
                id="cadastro-email"
                ref={emailRef}
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={invalido('email')}
                aria-describedby={descrito}
              />
            </div>

            {/* A senha é definida em /complete-signup, depois do checkout, onde
                existe confirmação e checagem de força. */}
            <p className="cadastro-depois" id="cadastro-depois">
              {t('cadastro.depois')}
            </p>

            {erro && (
              <p className="entrada-erro" id="cadastro-erro" role="alert">
                {erro.chave === 'cadastro.emailExiste' ? (
                  <>
                    {t('cadastro.emailExiste')} <Link to="/login">{t('cadastro.irEntrar')}</Link>{' '}
                    {t('cadastro.ou')} <Link to="/forgot-password">{t('cadastro.irSenha')}</Link>.
                  </>
                ) : (
                  t(erro.chave)
                )}
              </p>
            )}

            <button
              type="submit"
              className="sp-btn sp-btn-on-section nh-btn entrada-entrar"
              disabled={enviando}
              aria-describedby="cadastro-depois cadastro-consentimento"
            >
              {t(enviando ? 'cadastro.abrindoStripe' : 'cadastro.botao')}
            </button>

            {/* Uma frase só para os dois caminhos, logo abaixo do último botão,
                como o consentimento sempre esteve: junto do botão. Montada em
                pedaços porque os dois nomes são links e `t` devolve string. */}
            <p className="cadastro-consentimento" id="cadastro-consentimento">
              {t('cadastro.consentimentoA')} <Link to="/terms-of-service">{t('site.termos')}</Link>{' '}
              {t('cadastro.consentimentoE')} <Link to="/privacy-policy">{t('site.privacidade')}</Link>.
            </p>
          </form>

          <p className="entrada-conta">
            {t('cadastro.jaTemConta')} <Link to="/login">{t('cadastro.entrar')}</Link>
          </p>
          <SeletorDeIdioma />
        </div>
      </main>
    </div>
  );
};

export default NewSignup;
